/* because the property names used in contentful dont match the cookie */

import type { LocaleType } from "lib/apollo/types"
import type {
  FooterContent,
  Item,
  MerchandisingCategory,
  ExtraItems,
} from "types"
import { storeToLocaleMap } from "utils/i18n"

const contentfulNavItemsIds: Record<string, string> = {
  signIn: "2Uv2DsKxl605M6QPWkYEZ9",
  signOut: "P0EwUbnakPc8YwN4enVxJ",
}

const handleSubCategoryItems = (
  userType: "member" | "non-member",
  filteredItems?: Item[],
  isLapsedMember?: boolean
): Item[] => {
  if (!filteredItems?.length) {
    return []
  }

  // if user is a lapsed member we do not want to show "Sign In" button, but we want to show "Sign Out" button
  if (isLapsedMember) {
    return filteredItems?.filter(
      (filteredItem) => filteredItem.id !== contentfulNavItemsIds["signIn"]
    )
  }

  // if user is not a member we do not want to show "Sign Out" button
  if (userType === "non-member") {
    return (
      filteredItems?.filter(
        (filteredItem) => filteredItem.id !== contentfulNavItemsIds["signOut"]
      ) || []
    )
  }

  return filteredItems
}

export const filterSubCategoriesByUserType = (
  moreMenu: MerchandisingCategory | undefined,
  userType: "member" | "non-member",
  isLapsedMember?: boolean
): MerchandisingCategory | undefined => {
  if (!moreMenu) return undefined
  if (!moreMenu.items?.[0]) return moreMenu
  return {
    ...moreMenu,
    items: [
      {
        ...moreMenu.items?.[0],
        subCategories: moreMenu.items?.[0]?.subCategories?.reduce(
          (acc: Item[], subCat: Item) => {
            if (!subCat) return acc
            const filteredItems = subCat?.items?.filter(
              (item) => !item.visibility || item.visibility === userType
            )
            const newFilteredItems = handleSubCategoryItems(
              userType,
              filteredItems,
              isLapsedMember
            )
            return newFilteredItems?.length
              ? [...acc, { ...subCat, items: newFilteredItems }]
              : acc
          },
          []
        ),
      },
    ],
  }
}

export const filterNonMemberItems = (
  data: FooterContent | null
): FooterContent | null => {
  if (!data) return null
  return {
    ...data,
    categories: data.categories.map((category) => {
      return {
        ...category,
        items: category.items.filter(
          (item) => item.href.endsWith("login") === false
        ),
      }
    }),
  }
}

export function filterTopLevelItemsByUserType(
  topLevelItems: MerchandisingCategory[] | undefined,
  menuUserType: "member" | "non-member"
) {
  if (!topLevelItems) {
    return topLevelItems
  }
  return topLevelItems?.filter((item) => {
    return !item.visibility || item?.visibility === menuUserType
  })
}

const storePrefix = {
  "en-GB": "",
  "en-US": "/us",
}

export const withStorePrefix = (path: string, locale: LocaleType) =>
  `${storePrefix[locale]}${path}`

const pathReplacer = (locale: LocaleType) => (key: string, value: string) => {
  if (
    key === "url" &&
    value.startsWith("/") &&
    !value.startsWith(`/${locale}/`)
  ) {
    return withStorePrefix(value, locale)
  }
  return value
}

export const transformNavLinks = (
  data: FooterContent | null | undefined,
  locale: LocaleType
) => {
  if (!data) return null
  return JSON.parse(JSON.stringify(data, pathReplacer(locale))) as FooterContent
}

const getLocalisedTopCategories = (categories, locale) => {
  return categories.map((cat) => {
    return {
      ...cat,
      url: withStorePrefix(cat.url, locale),
    }
  })
}

const topCategoryHardcodedLinks = [
  {
    name: "Skincare",
    url: "/shop/skincare",
  },
  {
    name: "Makeup",
    url: "/shop/makeup",
  },
  {
    name: "Bodycare",
    url: "/shop/body",
  },
  {
    name: "Haircare",
    url: "/shop/hair-care",
  },
  {
    name: "Kits",
    url: "/shop/kits",
  },
]

export const addTopCategories = (
  items: MerchandisingCategory[] | undefined,
  locale: LocaleType
): MerchandisingCategory[] | undefined => {
  if (!items) return undefined
  return items?.map((obj) => ({
    ...obj,
    items: obj.items
      ? obj.items.map((subCat) => ({
          ...subCat,
          options:
            subCat.options &&
            Array.isArray(subCat.options) &&
            subCat.options.length
              ? subCat.options.map((option) => {
                  return {
                    ...option,
                    topCategories: getLocalisedTopCategories(
                      topCategoryHardcodedLinks,
                      locale
                    ),
                  }
                })
              : subCat.options,
        }))
      : obj.items,
  }))
}

export const withoutStorePrefix = (slug: string) => {
  const [first, ...rest] = slug.split("/")
  return first in storeToLocaleMap ? rest.join("/") : slug
}

export const filterExtraItemsByUserType = (
  userType: "member" | "non-member",
  extraItems?: ExtraItems[]
): ExtraItems[] => {
  if (!extraItems?.length) {
    return []
  }

  return extraItems.filter(
    (extraItem) => !extraItem.visibility || extraItem.visibility === userType
  )
}
